import { Routes, Route } from "react-router-dom";
import { useState } from "react";
import DriveAuth from "./components/DriveAuth";

import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";

// 🧩 Páginas principales
import Login from "./Login";
import ProtectedRoute from "./ProtectedRoute";

// 🧩 Dashboards
import Admin from "./dashboards/Admin";
import Psicologia from "./dashboards/Psicologia";
import Logopedia from "./dashboards/Logopedia";
import Nutricion from "./dashboards/Nutricion";

// 🧩 Vistas dentro de los dashboards
import AdminHome from "./dashboards/Admin/AdminHome";
import PacienteForm from "./dashboards/Admin/PacienteForm";
import PacientesList from "./components/PacientesList";
import CalendarPage from "./components/CalendarPage";
import AgendarSesion from "./components/AgendarSesion";
import PagosView from "./components/PagosView";
import FacturaView from "./components/FacturaView";

// 🧩 Especialidades específicas
import PsicologiaHome from "./dashboards/Psicologia/PsicologiaHome";
import LogopediaHome from "./dashboards/Logopedia/LogopediaHome";
import NutricionHome from "./dashboards/Nutricion/NutricionHome";
import PacientesListLogopedia from "./components/PacienteListLogopedia";
import PacientesListPsicologia from "./components/PacientesListPsicologia";
import PacientesListNutricionista from "./components/PacientesListNutricionista";
import NuevoUsuarioView from "./components/NuevoUsuarioView";
import UsuariosView from "./components/UsuariosView";
import EditarUsuariosView from "./components/EditarUsuariosView"; // 👈 asegúrate del nombre del archivo
import PagosProfesionalView from "./components/PagosProfesionalView";
import ResourcePage from "./components/ResourcePage";

// Juegos
import ColorMatch from "./games/ColorMatch/ColorMatch";
import GoNoGo from "./games/GoNoGo/GoNoGo";
import ColorSequence from "./games/ColorSequence/ColorSequence";
import InsideOutside from "./games/InsideOutside/InsideOutside";
import MissingPieces from "./games/MissingPieces/MissingPieces";
import MissingOne from "./games/MissingOne/MissingOne";
import MissingWord from "./games/MissingWord/MissingWord";
import NumberKey from "./games/NumberKey/NumberKey";
import OddDrawing from "./games/OddDrawing/OddDrawing";
import OddLetter from "./games/OddLetter/OddLetter";
import ObjectDetection from "./games/ObjectDetection/ObjectDetection";
import RepeatedDrawings from "./games/RepeatedDrawings/RepeatedDrawings";
import FindTargetObject from "./games/FindTargetObject/FindTargetObject";
import FindUniqueObject from "./games/FindUniqueObject/FindUniqueObject";
import RepeatedWords from "./games/RepeatedWords/RepeatedWords";
import SequenceCancellation from "./games/SequenceCancellation/SequenceCancellation";
import MissingNumber from "./games/MissingNumber/MissingNumber";

import PublicBooking from "./components/PublicBooking";

// 🧩 Componentes compartidos
import PacienteView from "./components/PacienteView";

import "@fortawesome/fontawesome-free/css/all.min.css";

export default function App() {
  const [user, setUser] = useState(null);

  return (
    <Routes>
      {/* 🟢 Página de login */}
      <Route path="/" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/reservar" element={<PublicBooking />} />
      {/* 🔒 ADMIN */}
      <Route element={<ProtectedRoute allowRoles={["admin"]} />}>
        <Route path="/admin" element={<Admin />}>
          <Route index element={<AdminHome />} />
          <Route path="paciente" element={<PacienteForm />} />
          <Route
            path="pacientes"
            element={<PacientesList userRole="admin" />}
          />
          <Route path="calendario" element={<CalendarPage />} />
          <Route path="sesion" element={<AgendarSesion />} />
          <Route path="pagos" element={<PagosView user={user} />} />
          <Route path="factura/:id" element={<FacturaView />} />

          {/* 👇 Gestión de usuarios */}
          {/* /admin/usuarios → pestañas Listado / Nuevo */}
          <Route path="usuarios" element={<UsuariosView />} />
          {/* /admin/usuarios/nuevo → acceso directo solo al form si quieres usarlo */}
          <Route path="usuarios/nuevo" element={<NuevoUsuarioView />} />
          {/* /admin/usuarios/:id → edición de un usuario */}
          <Route path="usuarios/:id" element={<EditarUsuariosView />} />
        </Route>
      </Route>

      {/* 🔒 PSICOLOGÍA */}
      <Route element={<ProtectedRoute allowRoles={["psicologa"]} />}>
        <Route path="/psicologia" element={<Psicologia />}>
          <Route index element={<PsicologiaHome />} />
          <Route
            path="pacientes-psicologia"
            element={<PacientesListPsicologia userRole="psicologa" />}
          />
          <Route path="calendario" element={<CalendarPage />} />
          <Route path="pagos" element={<PagosProfesionalView />} />
        </Route>
      </Route>

      {/* 🔒 LOGOPEDIA */}
      <Route element={<ProtectedRoute allowRoles={["logopeda"]} />}>
        <Route path="/logopedia" element={<Logopedia />}>
          <Route index element={<LogopediaHome />} />
          <Route
            path="pacientes-logopedia"
            element={<PacientesListLogopedia userRole="logopeda" />}
          />
          <Route path="calendario" element={<CalendarPage />} />
          <Route path="pagos" element={<PagosProfesionalView />} />
        </Route>
      </Route>

      {/* 🔒 NUTRICIÓN */}
      <Route element={<ProtectedRoute allowRoles={["nutricionista"]} />}>
        <Route path="/nutricion" element={<Nutricion />}>
          <Route index element={<NutricionHome />} />
          <Route
            path="pacientes-nutricion"
            element={<PacientesListNutricionista userRole="nutricionista" />}
          />
          <Route path="calendario" element={<CalendarPage />} />
          <Route path="pagos" element={<PagosProfesionalView />} />
        </Route>
      </Route>

      {/* 🟦 PACIENTE / RECURSOS / JUEGOS */}
      <Route
        element={
          <ProtectedRoute
            allowRoles={["admin", "psicologa", "logopeda", "nutricionista"]}
          />
        }
      >
        <Route path="/paciente/:id" element={<PacienteView />} />

        <Route path="/recursos" element={<ResourcePage />} />

        <Route path="/games/color_match" element={<ColorMatch />} />
        <Route path="/games/go_no_go" element={<GoNoGo />} />
        <Route path="/games/color_sequence" element={<ColorSequence />} />
        <Route path="/games/inside_outside" element={<InsideOutside />} />
        <Route path="/games/missing_pieces" element={<MissingPieces />} />
        <Route path="/games/missing_one" element={<MissingOne />} />
        <Route path="/games/missing_word" element={<MissingWord />} />
        <Route path="/games/number_key" element={<NumberKey />} />
        <Route path="/games/odd_drawing" element={<OddDrawing />} />
        <Route path="/games/odd_letter" element={<OddLetter />} />
        <Route path="/games/object_detection" element={<ObjectDetection />} />
        <Route path="/games/repeated_drawings" element={<RepeatedDrawings />} />
        <Route
          path="/games/find_unique_object"
          element={<FindUniqueObject />}
        />
        <Route
          path="/games/find_target_object"
          element={<FindTargetObject />}
        />
        <Route path="/games/repeated_words" element={<RepeatedWords />} />
        <Route
          path="/games/sequence_cancellation"
          element={<SequenceCancellation />}
        />
        <Route path="/games/missing_number" element={<MissingNumber />} />
      </Route>

      {/* 🧾 FACTURA GLOBAL (admin + profesionales) */}
      <Route
        element={
          <ProtectedRoute
            allowRoles={["admin", "psicologa", "logopeda", "nutricionista"]}
          />
        }
      >
        <Route path="/factura/:id" element={<FacturaView />} />
      </Route>

      {/* 🔑 Autenticación de Google Drive */}
      <Route path="/auth/drive" element={<DriveAuth />} />

      {/* 🔴 Fallback: cualquier ruta no reconocida → Login */}
      <Route path="*" element={<Login />} />
    </Routes>
  );
}
